import { Component, ElementRef, EventEmitter, Input, OnDestroy, Output, QueryList, ViewChildren } from '@angular/core';
import { FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { AppConfigService } from '@app/core/services/app-config.service';
import { MultiFieldEditorContent } from '@app/order/models/multi-field-editor-content';
import { AdContentElement } from '@core/models/ad-content-element.model';
import { GalleryImage } from '@core/models/gallery-image';
import { StyleElement } from '@core/models/style-element';
import { FormattingService } from '@core/services/formatting.service';
import { ImageFilteringOptions, ImageGalleryService } from '@core/services/image-gallery.service';
import { Logger } from '@core/services/logger.service';
import { ImageGalleryDialogComponent } from '@shared/components/image-gallery-dialog/image-gallery-dialog.component';
import { CassValidators } from '@shared/models/cass-validators';
import { ImageGalleryItem } from '@shared/models/image-gallery-item';
import { MultiField } from '@shared/models/multi-field';
import { MultiFieldContent } from '@shared/models/multi-field-content';
import { MultiFieldImageSource } from '@shared/models/multi-field-image-source';
import { MultiFieldType } from '@shared/models/multi-field-type';
import { MultiFieldTextEditorCacheService } from '@shared/services/multi-field-text-editor-cache.service';
import { UserAlertService } from '@shared/services/user-alert.service';
import { ImageCroppedEvent } from 'ngx-image-cropper';
import { Subscription } from 'rxjs';

export enum MultiFieldActiveTool {
	PREVIEW = 0,
	CROP
}

// This wrapper class is required to prevent an issue with field validators.
// When user switches among multi-field styles, Angular does not re-render the fields
// which existed on the previous form.
// So the MultiFieldViewModel(s) cannot be easily re-used when copied from the cache service.
// To avoid copying of potentially big MultiFieldViewModel(s), this wrapper is introduced.
// Each time the fields are rendered, new wrapper of the same field makes Angular re-render it.
export class MultiFieldViewModel {
	constructor(multiFieldModel: MultiField) { // cropResizeConstraints?: CropResizeConstraints) {
		this.model = multiFieldModel;
		this.activeImageTool = MultiFieldActiveTool.PREVIEW;
		this.allowImageToolCrop = false;
	}

	model: MultiField;
	activeImageTool?: MultiFieldActiveTool;
	allowImageToolCrop: boolean;
}

class CroppedImageInfo {
	private imageContent: string;
	private imageWidth: number;
	private imageHeight: number;

	constructor(content: string, imageWidth: number, imageHeight: number) {
		this.imageContent = content;
		this.imageWidth = imageWidth;
		this.imageHeight = imageHeight;
	}

	get content(): string {
		return this.imageContent;
	}

	get width(): number {
		return this.imageWidth;
	}

	get height(): number {
		return this.imageHeight;
	}
}

type InsertableSymbol = {
	insert: string,
	label: string
}

@Component({
	selector: 'cass-multi-field-text-editor',
	templateUrl: './multi-field-text-editor.component.html',
	styleUrls: ['./multi-field-text-editor.component.css']
})
export class MultiFieldTextEditorComponent implements OnDestroy {

	// High-level perspective:
	// For each multi-form field, create a view model and a control.
	// Attach event handler to each form field
	// Trigger output report upon:
	// - any non-image events: trigger output with debounce
	// - image clear: clear immediately and trigger output with debounce
	// - image uploading: upload the image and then trigger output without debounce

	private readonly COMPONENT_NAME = "MultiFieldTextEditorComponent";

	@Input()
	set templateElements(elements: StyleElement[] | undefined) {
		// Note: the styles are selected dynamically; the fields may contain user-entered data which should be preserved
		this.clearControlsSubscriptions();

		if (this.multiFieldTextEditorForm) {
			this.multiFieldTextEditorForm.reset();
			for (const name in this.multiFieldTextEditorForm.controls) {
				this.multiFieldTextEditorForm.removeControl(name);
			}
		}
		this.fields = this.createViewModels(elements, this.initialContent);
		this.multiFieldTextEditorForm = this.createForm(elements, this.fields);

		// trigger the output report as the fields may have all required values filled in
		// and the user just switches the styles
		this.triggerOutputWithDebounce();

		// controls appearance of the validation errors for empty fields when user switched styles
		this.cacheService.setFormHasBeenDisplayed(true);
	}

	@Input()
	initialContent: MultiFieldContent[] | undefined;

	@Input()
	set packageId(id: string | undefined) {
		if (this._packageId !== id) {
			this._packageId = id;
			this.removeInvalidLibraryImages();
		}
	}
	get packageId() {
		return this._packageId;
	}
	private _packageId?: string = undefined;

	@Input()
	set placementId(id: string | undefined) {
		if (this._placementId !== id) {
			this._placementId = id;
			this.removeInvalidLibraryImages();
		}
	}
	get placementId(): string | undefined {
		return this._placementId;
	}
	private _placementId?: string = undefined;

	@Output()
	multiFieldContentChanged = new EventEmitter<MultiFieldEditorContent>();

	@ViewChildren("cassFileInput")
	fileInputElements!: QueryList<ElementRef>;

	multiFieldTextEditorForm: FormGroup;

	fields: MultiFieldViewModel[] = [];

	insertableSymbols: InsertableSymbol[] = [];

	// Make the following types available for use in the component template
	MultiFieldType = MultiFieldType;
	MultiFieldActiveTool = MultiFieldActiveTool;

	constructor(private logger: Logger,
		private cacheService: MultiFieldTextEditorCacheService,
		private imageGalleryService: ImageGalleryService,
		private dialog: MatDialog,
		private userAlertService: UserAlertService,
		private appConfig: AppConfigService) {

		this.initInsertableSymbols();
		this.multiFieldTextEditorForm = new FormGroup({});

	}

	private subscriptions$: Subscription[] = [];
	ngOnDestroy(): void {
		this.clearControlsSubscriptions();
	}

	private initInsertableSymbols() {
		// Format of config.editorSymbols is
		// E.g. ["°","€",["\t","⇥"]]
		// Each element is a string or an array (tuple) with two string elements.
		// In the above example, the third button shows ⇥ and inserts a tab character (\t is the javascript escape sequence for tab character).
		// The model is of type InsertableSymbol[] which is an alias of {insert:string, label:string}[]
		this.appConfig.config?.editorSymbols?.forEach(elem => {
			if (elem instanceof Array) {
				this.insertableSymbols.push({ insert: elem[0], label: elem[1] })
			}
			else {
				this.insertableSymbols.push({ insert: elem, label: elem });
			}
		});
	}

	private createViewModels(styleElements: StyleElement[] | undefined, initialContent: MultiFieldContent[] | undefined): MultiFieldViewModel[] {

		const fieldVMs: MultiFieldViewModel[] = [];

		if (styleElements && styleElements.length > 0) {
			for (const styleElement of styleElements) {
				let cacheItem = this.cacheService.getItem(styleElement.key);
				if (!cacheItem) {
					cacheItem = initialContent?.find((o) => o.key === styleElement.key);
					if (!cacheItem) {
						cacheItem = new MultiFieldContent(styleElement.key);
					}
					this.cacheService.addItem(styleElement.key, cacheItem);
				}

				const multiField = new MultiField(styleElement, cacheItem);
				const fieldVM = new MultiFieldViewModel(multiField);

				this.initAllowedImageTools(fieldVM);
				if (multiField.type === MultiFieldType.IMAGE && this.doesImageExceedSizeConstraints(fieldVM)) {
					fieldVM.activeImageTool = MultiFieldActiveTool.CROP;
				}

				fieldVMs.push(fieldVM);
			}
		}

		return fieldVMs;
	}

	private createForm(elements: StyleElement[] | undefined, fields: MultiFieldViewModel[]): FormGroup {

		let isAnyFieldFilledIn = false;
		const form = new FormGroup({});

		if (elements && fields && fields.length > 0) {
			for (let i = 0; i < fields.length; ++i) {
				const field: MultiFieldViewModel = fields[i];

				const control = new FormControl();
				this.setControlValue(control, field.model);
				this.setControlValidators(control, field.model);

				const subscription$ = control.valueChanges.subscribe((val) => {
					this.formFieldValueChanged(val, control, field.model);
				});

				this.subscriptions$.push(subscription$);

				form.addControl(field.model.key, control);

				if (control.value) {
					isAnyFieldFilledIn = true;
				}
			}
		}
		else {
			this.logger.error(this.COMPONENT_NAME, "Selected style does not contain any elements, the created form is empty");
		}

		if (this.cacheService.getFormHasBeenDisplayed()) {
			if (isAnyFieldFilledIn) {
				form.markAllAsTouched();
			}
		}

		return form;
	}

	private setControlValue(control: FormControl, field: MultiField): void {
		if (field.type === MultiFieldType.IMAGE) {
			// For Image field, the value is the file name
			control.setValue(field.fileName);
		}
		else {
			// For non-Image field, the value is the content
			if (field.content) {
				control.setValue(field.content);
				// when the control has any data, it should indicate if not valid
				control.markAsTouched({ onlySelf: true });
			}
		}
	}

	private setControlValidators(control: FormControl, field: MultiField): void {
		const validators: ValidatorFn[] = [];

		if (field.config.maxlength && field.config.maxlength > 0) {
			validators.push(Validators.maxLength(field.config.maxlength))
		}

		if (field.config.minlength && field.config.minlength > 0) {
			validators.push(Validators.minLength(field.config.minlength));
		}

		if (field.config.required) {
			if (field.type === MultiFieldType.IMAGE) {
				validators.push(CassValidators.imageRequired(field));
			}
			else {
				validators.push(Validators.required);
			}
		}

		if (validators.length > 0) {
			control.setValidators(validators);
		}
	}

	private formFieldValueChanged(val: any, control: FormControl, field: MultiField): void {

		if (field.type !== MultiFieldType.IMAGE) {
			field.setText(control.value);
			this.triggerOutputWithDebounce();
		}
	}

	private timerId: any = undefined;
	private triggerOutputWithDebounce(isImmediate = false): void {
		if (this.timerId) {
			clearTimeout(this.timerId);
			this.timerId = undefined;
		}

		const timeoutValue = isImmediate ? 0 : 500;
		this.timerId = setTimeout(() => {
			this.reportOutput();
		}, timeoutValue);
	}

	removeImg(fieldVM: MultiFieldViewModel): void {

		if (fieldVM && this.multiFieldTextEditorForm) {
			fieldVM.model.removeImage();
			const control = this.multiFieldTextEditorForm.get(fieldVM.model.key);
			if (control) {
				control.setValue(undefined);
				control.markAsTouched();
			}
		}

		this.clearFileInputControl(fieldVM.model.key);
		this.triggerOutputWithDebounce(true);
	}

	private clearFileInputControl(key: string): void {
		const fileInputControl = this.fileInputElements.find((fileControl) => {
			return fileControl && fileControl.nativeElement && fileControl.nativeElement.name === key;
		});

		if (fileInputControl) {
			// The native input file control should be reset to allow selection of the same file once again
			fileInputControl.nativeElement.value = "";
		}
	}

	startImgToolCrop(fieldWrapper: MultiFieldViewModel): void {
		fieldWrapper.activeImageTool = MultiFieldActiveTool.CROP;
	}

	private cropBufferMap = new Map<string, CroppedImageInfo>();

	imgCropped(field: MultiField, event: ImageCroppedEvent): void {
		// Keep a copy of the cropped image in the buffer; the user may continue to use the tool to crop it further
		if (field) {
			if (event.base64) {
				const croppedImage = new CroppedImageInfo(event.base64, event.width, event.height);
				this.cropBufferMap.set(field.key, croppedImage);
			}
			else {
				this.cropBufferMap.delete(field.key);
			}
		}
	}

	toolCommandDoCrop(fieldVM: MultiFieldViewModel): void {
		// The user has finished to work with the crop tool; copy the image from the buffer into the field.content
		if (this.cropBufferMap.has(fieldVM.model.key)) {
			const croppedImage = this.cropBufferMap.get(fieldVM.model.key);
			this.cropBufferMap.delete(fieldVM.model.key);

			fieldVM.model.setUploadedImage(croppedImage?.content, fieldVM.model.fileName, croppedImage?.width, croppedImage?.height);

			fieldVM.activeImageTool = MultiFieldActiveTool.PREVIEW;

			this.triggerOutputWithDebounce();
		}
	}

	startImageReader(fieldWrapper: MultiFieldViewModel): void {
		// visible UI button "Choose file..." invokes hidden native input file control

		if (fieldWrapper) {
			const fileInputControl = this.fileInputElements.find((fileControl) => {
				return fileControl && fileControl.nativeElement && fileControl.nativeElement.name === fieldWrapper.model.key;
			});

			if (fileInputControl) {
				fileInputControl.nativeElement.click();
			}
		}
	}

	handleFileInput(fieldVM: MultiFieldViewModel, filesTarget: any) {

		if (filesTarget && filesTarget.files && filesTarget.files.length > 0) {
			const file: File = filesTarget.files[0];
			const reader = new FileReader();
			reader.onload = (e: ProgressEvent<FileReader>) => {
				if (fieldVM.model.config.maxsize && file.size > fieldVM.model.config.maxsize) {
					fieldVM.model.content = undefined;
					const control = this.multiFieldTextEditorForm.get(fieldVM.model.key);
					if (control) {
						const fileSizeError = CassValidators.createCassGenericError("FORMS_FIELDS_VALIDATION_ERRORS.FILE_TOO_BIG", { maxsize: FormattingService.formatFileSize(fieldVM.model.config.maxsize) });
						console.log("Max file size constraint, multi-field component");
						control.setErrors(fileSizeError);
						control.markAsTouched();

						this.clearFileInputControl(fieldVM.model.key);
						return;
					}
				}
				else {
					if (fieldVM.model.cropResizeConstraints && (fieldVM.model.cropResizeConstraints.hasMinHWLimits || fieldVM.model.cropResizeConstraints.hasMaxHWLimits)) {
						const image = new Image();
						image.src = e.target?.result as string;
						image.onload = rs => {
							// console.log("image loaded, dimensions:", { height: image.height, width: image.width });

							// check min requirements and fail validation if not met
							let imageSizeError: ValidationErrors | null = null;
							if (fieldVM && fieldVM.model.cropResizeConstraints && (fieldVM.model.cropResizeConstraints?.cropMinWidth > 0 || fieldVM.model.cropResizeConstraints?.cropMinHeight > 0)) {
								if (image.width < fieldVM.model.cropResizeConstraints.cropMinWidth && image.height < fieldVM.model.cropResizeConstraints.cropMinHeight) {
									// The image must be at least WxH pixels (width x height).
									imageSizeError = CassValidators.createCassGenericError("FORMS_FIELDS_VALIDATION_ERRORS.UPLOADED_IMAGE_INVALID_MIN_WIDTH_HEIGHT", { minWidth: fieldVM.model.cropResizeConstraints?.cropMinWidth, minHeight: fieldVM.model.cropResizeConstraints?.cropMinHeight });
								}
								else if (image.width < fieldVM.model.cropResizeConstraints.cropMinWidth) {
									// The image must be at least W pixels wide.
									imageSizeError = CassValidators.createCassGenericError("FORMS_FIELDS_VALIDATION_ERRORS.UPLOADED_IMAGE_INVALID_MIN_WIDTH", { minWidth: fieldVM.model.cropResizeConstraints?.cropMinWidth });
								}
								else if (image.height < fieldVM.model.cropResizeConstraints.cropMinHeight) {
									// The image must be at least H pixels high.
									imageSizeError = CassValidators.createCassGenericError("FORMS_FIELDS_VALIDATION_ERRORS.UPLOADED_IMAGE_INVALID_MIN_HEIGHT", { minHeight: fieldVM.model.cropResizeConstraints?.cropMinHeight });
								}
							}

							const control = this.multiFieldTextEditorForm.get(fieldVM.model.key);
							if (control) {
								if (imageSizeError) {
									control.setErrors(imageSizeError);
									control.markAsTouched();
									return;
								}
								else {
									// clear the errors set by the previous image
									control.setErrors(null);
								}
							}

							fieldVM.model.setUploadedImage(e.target?.result as string, file.name, image.width, image.height);

							// Note: the method requires proper values in fieldVM.model.imageWidth and fieldVM.model.imageHeight
							const isCropperRequired = this.doesImageExceedSizeConstraints(fieldVM);
							if (isCropperRequired) {

								fieldVM.activeImageTool = MultiFieldActiveTool.CROP;
							}

							this.finalizeFileInput(fieldVM, file);
						}
					}
					else {
						// no min limits, load content as usual
						fieldVM.model.content = e.target?.result as string;
						this.finalizeFileInput(fieldVM, file);
					}
				}
			};
			reader.onabort = () => {
				this.triggerOutputWithDebounce();
			}
			reader.onerror = () => {
				this.triggerOutputWithDebounce();
			}
			reader.readAsDataURL(file);
		}
	}

	private doesImageExceedSizeConstraints(fieldVM: MultiFieldViewModel) {
		let res = false;

		if (fieldVM?.model.cropResizeConstraints) {
			if (fieldVM.model.cropResizeConstraints?.cropMaxWidth > 0) {
				if (fieldVM.model.imageWidth && fieldVM.model.imageWidth > 0) {
					if (fieldVM.model.imageWidth > fieldVM.model.cropResizeConstraints?.cropMaxWidth) {
						res = true;
					}
				}
			}

			if (fieldVM.model.cropResizeConstraints?.cropMaxHeight > 0) {
				if (fieldVM.model.imageHeight && fieldVM.model.imageHeight > 0) {
					if (fieldVM.model.imageHeight > fieldVM.model.cropResizeConstraints?.cropMaxHeight) {
						res = true;
					}
				}
			}
		}

		return res;
	}

	private finalizeFileInput(fieldVM: MultiFieldViewModel, file: File): void {
		this.setFileInputValue(fieldVM.model.key, file.name);
		this.initAllowedImageTools(fieldVM);
		this.triggerOutputWithDebounce();
	}

	startImageGallery(fieldVM: MultiFieldViewModel): void {
		// get list of images for given package and placement
		// for each image, buld an ImageGalleryItem and pass into the dialog

		if (this.packageId && this.placementId) {
			const imageFilteringOptions = this.createImageFilteringOptions(fieldVM);
			this.imageGalleryService.getGalleryImagesForPackageAndPlacement(this.packageId, this.placementId, imageFilteringOptions).subscribe(
				(galleryImages) => {
					this.showImageGallery(fieldVM, galleryImages);
				},
				(error) => {
					this.logger.error(this.COMPONENT_NAME, `getGalleryImagesForPackageAndPlacement() failed for packageId="${this.packageId}" and placementId="${this.placementId}"`, error);
					this.userAlertService.errorByTranslationKey("ERRORS.CANNOT_RETRIEVE_GALLERY_IMAGES_LIST");
				},
			);
		}
	}

	private showImageGallery(fieldWrapper: MultiFieldViewModel, galleryImages: GalleryImage[]) {
		const gis = galleryImages.map((gi) => {
			if (gi.id) {
				const imageItem: ImageGalleryItem = new ImageGalleryItem();
				imageItem.id = gi.id;
				imageItem.label = gi.label;
				imageItem.url = this.imageGalleryService.createGalleryImageThumbnailURL(gi.id);
				return imageItem;
			}
			return null;
		});

		if (gis && gis.length > 0) {
			const dialogRef = this.dialog.open(ImageGalleryDialogComponent, { data: gis });
			dialogRef.afterClosed().subscribe(result => {
				if (result) {
					// an image is selected
					const imageGalleryItem = result as ImageGalleryItem;
					this.logger.info(this.COMPONENT_NAME, `User has selected the gallery image id="${imageGalleryItem.id}"`);
					this.updateFieldWithGalleryImage(fieldWrapper, result);
				}
				else {
					this.logger.info(this.COMPONENT_NAME, "User cancelled the gallery image selection");
				}
			});
		}
		else {
			// there are no images configured for this placement
			this.logger.warning(this.COMPONENT_NAME, `There are no library images configured for packageId="${this.packageId}" and placementId="${this.placementId}", constraints: minwidth=${fieldWrapper.model.cropResizeConstraints?.cropMinWidth || "N/A"}, maxwidth=${fieldWrapper.model.cropResizeConstraints?.cropMaxWidth || "N/A"}, minheight=${fieldWrapper.model.cropResizeConstraints?.cropMinHeight || "N/A"}, maxheight=${fieldWrapper.model.cropResizeConstraints?.cropMaxHeight || "N/A"}`);
			this.userAlertService.errorByTranslationKey("ORDER.IMAGES.NO_IMAGES_FOR_PLACEMENT");
		}
	}

	private updateFieldWithGalleryImage(fieldVM: MultiFieldViewModel, imageGalleryItem: ImageGalleryItem) {
		if (imageGalleryItem.id) {
			this.imageGalleryService.downloadHighresGalleryImage(imageGalleryItem.id).subscribe((blob) => {
				const reader = new FileReader();

				reader.onload = () => {
					const dataurl = reader.result;
					fieldVM.model.setGalleryImage(imageGalleryItem.id, dataurl?.toString(), imageGalleryItem.label);
					this.setFormImageName(fieldVM.model.key, imageGalleryItem.label);
					this.initAllowedImageTools(fieldVM);
					this.triggerOutputWithDebounce();
				};

				reader.readAsDataURL(blob); // converts the blob to base64 and calls onload
			});
		}
	}

	private initAllowedImageTools(fieldVM: MultiFieldViewModel) {
		fieldVM.allowImageToolCrop = false;

		// Business Rule: allow Image Crop for imaged loaded from disk.
		if (fieldVM.model.content && fieldVM.model.imageSource === MultiFieldImageSource.Upload) {
			fieldVM.allowImageToolCrop = true;
		}
	}

	private setFileInputValue(key: string, value: string): void {
		const control = this.multiFieldTextEditorForm.get(key);
		if (control) {
			control.setValue(value);
		}
	}

	private setFormImageName(key: string, name?: string): void {
		const control = this.multiFieldTextEditorForm.get(key);
		if (control) {
			control.setValue(name);
		}
	}

	private reportOutput(): void {
		const res = new MultiFieldEditorContent();
		res.isComplete = this.multiFieldTextEditorForm.valid;

		for (const field of this.fields) {
			const fieldValue = this.multiFieldTextEditorForm.value[field.model.key];

			if (fieldValue) {
				const adContentElement = new AdContentElement();
				adContentElement.key = field.model.key;

				if (field.model.type === MultiFieldType.IMAGE) {
					if (field.model.content) {
						// in case of file, take its content from the field where it was loaded
						adContentElement.content = field.model.content;
						//_this.previewDataURL = field.content;
						res.contents.push(adContentElement);
					}
					if (field.activeImageTool !== MultiFieldActiveTool.PREVIEW) {
						// special case: the image can be loaded but not yet cropped, so the validation must fail
						res.isComplete = false;
					}
				}
				else {
					adContentElement.content = fieldValue;
					res.contents.push(adContentElement);
				}
			}

			if (field.model.config.required && !field.model.content) {
				res.isComplete = false;
			}
		}

		this.multiFieldContentChanged.emit(res);
	}

	private clearControlsSubscriptions(): void {
		for (const subscription$ of this.subscriptions$) {
			subscription$.unsubscribe();
		}
		this.subscriptions$.length = 0; // clear
	}

	public insertText(control: HTMLElement, text: string): boolean {
		if (control instanceof HTMLTextAreaElement || control instanceof HTMLInputElement)
			control.setRangeText(text, control.selectionStart || 0, control.selectionEnd || 0, "end");
		control.dispatchEvent(new InputEvent('input', { data: text, inputType: 'insertText' }));
		control.focus();
		return false;
	}

	// When packageId or placementId is updated, existing gallery images could become invalid.
	// For each gallery image, this method requests the list of images for given packageId and placementId and field image filtering options.
	// If the existing image id is not in the list of allowed images, the gallery image is removed from the user selection.
	private removeInvalidLibraryImages() {
		if (this.packageId && this.placementId && this.fields.length > 0) {
			for (const fieldVM of this.fields) {
				if (fieldVM.model.imageSource === MultiFieldImageSource.Gallery) {
					// the gallery image of this field needs to be checked for validity with updated package and placement ids
					const imageFilteringOptions = this.createImageFilteringOptions(fieldVM);
					this.imageGalleryService.getGalleryImagesForPackageAndPlacement(this.packageId, this.placementId, imageFilteringOptions).subscribe(
						(galleryImages) => {
							const imageId = fieldVM.model.contentItem.galleryImageId;
							if (imageId) {
								if (!galleryImages.find(img => img.id === imageId)) {
									//
									this.removeImg(fieldVM);
								}
							}
						},
						(error) => {
							this.logger.error(this.COMPONENT_NAME, `removeInvalidLibraryImages(): getGalleryImagesForPackageAndPlacement() failed for packageId="${this.packageId}" and placementId="${this.placementId}"`, error);
						},
					);
				}
			}
		}
	}

	private createImageFilteringOptions(fieldVM: MultiFieldViewModel): ImageFilteringOptions {
		const imageFilteringOptions = new ImageFilteringOptions();

		if (fieldVM.model.cropResizeConstraints?.cropMinWidth) {
			imageFilteringOptions.minwidth = fieldVM.model.cropResizeConstraints.cropMinWidth;
		}

		if (fieldVM.model.cropResizeConstraints?.cropMaxWidth) {
			imageFilteringOptions.maxwidth = fieldVM.model.cropResizeConstraints.cropMaxWidth;
		}

		if (fieldVM.model.cropResizeConstraints?.cropMinHeight) {
			imageFilteringOptions.minheight = fieldVM.model.cropResizeConstraints.cropMinHeight;
		}

		if (fieldVM.model.cropResizeConstraints?.cropMaxHeight) {
			imageFilteringOptions.maxheight = fieldVM.model.cropResizeConstraints.cropMaxHeight;
		}

		imageFilteringOptions.elementKey = fieldVM.model.key;

		return imageFilteringOptions;
	}
}
